import React, { useEffect, useState, useCallback, useRef } from "react";
import { gsap } from "gsap";
import { 
  getTenantId, 
  parseArrayData, 
  isPrimaryOccupant,
  getRentInvoices,
  startRentPayment,
  verifyRentPayment,
  getPaymentHistory,
  startFoundationSupportPayment,
  verifyFoundationSupportPayment,
} from "./TenantAuth";
import { useTenantHeader } from "./TenantLayout";
import toast from "react-hot-toast";

const PAGE_SIZE = 15;

function fmtMoney(v) {
  if (v == null || v === "") return "—";
  return new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN", maximumFractionDigits: 0 }).format(Number(v));
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function useBootstrapModal(ref) {
  const show = useCallback(() => {
    if (!ref.current || !window.bootstrap?.Modal) return;
    window.bootstrap.Modal.getOrCreateInstance(ref.current, { backdrop: "static" }).show();
  }, [ref]);
  const hide = useCallback(() => {
    if (!ref.current || !window.bootstrap?.Modal) return;
    window.bootstrap.Modal.getInstance(ref.current)?.hide();
  }, [ref]);
  return { show, hide };
}

function ModalShell({ modalRef, icon, title, onClose, closeDisabled, subheader, footer, overlay, width = 720, fill = false, children }) {
  return (
    <div className="modal fade" ref={modalRef} tabIndex={-1} aria-hidden="true">
      <div className="modal-dialog modal-dialog-centered d6m-dialog" style={{ maxWidth: width }}>
        <div className="modal-content d6m">
          {overlay}
          <div className="d6m-head">
            <span className="d6m-head-icon"><i className={`ki-outline ${icon}`}></i></span>
            <div className="min-w-0 flex-grow-1">
              <h3 className="d6m-title text-truncate">{title}</h3>
            </div>
            <button type="button" className="d6m-x" onClick={onClose} disabled={closeDisabled} aria-label="Close">
              <i className="ki-outline ki-cross"></i>
            </button>
          </div>
          {subheader}
          <div className={`d6m-body ${fill ? "d6m-body-fill" : ""}`}>{children}</div>
          {footer && <div className="d6m-foot">{footer}</div>}
        </div>
      </div>
    </div>
  );
}

function StatusChip({ color = "#a1a1aa", children }) {
  return (
    <span className="d6m-chip" style={{ "--dot": color }}>
      {children}
    </span>
  );
}

function Field({ label, required, hint, error, children, className = "" }) {
  return (
    <div className={`d6m-field ${className}`}>
      <label>
        {label}
        {required && <span className="text-danger ms-1">*</span>}
      </label>
      {children}
      {error ? <div className="d6m-error">{error}</div> : hint ? <div className="d6m-hint">{hint}</div> : null}
    </div>
  );
}

function CardHead({ title, subtitle, children, className = "mb-4" }) {
  return (
    <div className={`d-flex align-items-start justify-content-between flex-wrap gap-3 ${className}`}>
      <div className="min-w-0">
        <h3 className="text-gray-900 d6-title mb-1">{title}</h3>
        {subtitle && <span className="text-gray-400 fs-8">{subtitle}</span>}
      </div>
      {children && <div className="d-flex align-items-center gap-2 flex-wrap">{children}</div>}
    </div>
  );
}

function PagePreloader({ visible }) {
  return (
    <div
      className={`page-preloader-overlay d6-topnav-preloader ${visible ? "is-visible" : ""}`}
      aria-hidden={!visible}
    >
      <div className="logo-box">
        <svg className="house-icon" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
          <path d="M 34 60 L 34 80 L 18 80 L 18 50 L 34 34 L 50 50 L 50 85" fill="none" stroke="#FFFFFF" strokeWidth="10" strokeLinecap="butt" strokeLinejoin="miter" />
          <path d="M 45.31 22.69 L 66 43.37 L 66 85" fill="none" stroke="#BBEE6B" strokeWidth="10" strokeLinecap="butt" strokeLinejoin="miter" />
          <path d="M 56.63 11.37 L 82 36.75 L 82 85" fill="none" stroke="#FFD75B" strokeWidth="10" strokeLinecap="butt" strokeLinejoin="miter" />
        </svg>
      </div>
    </div>
  );
}

const INVOICE_DOT = {
  paid: "#16A34A",
  pending: "#D97706",
  overdue: "#DC2626",
  cancelled: "#a1a1aa",
  partial: "#2563EB",
};

/**
 * Rent Payment Modal (Start Rent Payment & Verify Rent Payment)
 */
export function PaymentModal({ modalRef, invoice, onClose, onSuccess }) {
  const { hide } = useBootstrapModal(modalRef);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [gatewayRef, setGatewayRef] = useState("");
  const [step, setStep] = useState("start"); // 'start' | 'verify' | 'confirmed'
  const [copied, setCopied] = useState(false);
  const [startError, setStartError] = useState("");

  const handleClose = useCallback(() => {
    hide();
    setStep("start");
    setGatewayRef("");
    setStartError("");
    onClose?.();
  }, [hide, onClose]);

  // Start the payment once per invoice. Keyed on the invoice id so re-renders never re-call the API.
  const invoiceKey = invoice ? invoice.rentInvoiceId || invoice.id : null;
  useEffect(() => {
    if (!invoiceKey) return undefined;
    let mounted = true;
    setLoading(true);
    setStep("start");
    setStartError("");
    setPaymentData(null);
    startRentPayment(invoiceKey)
      .then((res) => { if (mounted) { setPaymentData(res); setLoading(false); } })
      .catch((err) => {
        if (mounted) {
          setStartError(err.message || "Failed to initiate payment.");
          setLoading(false);
        }
      });
    return () => { mounted = false; };
  }, [invoiceKey]);

  const handleCopy = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("Payment reference copied.");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleOpenPayU = () => {
    const url = paymentData?.checkoutUrl || `https://secure.payu.co.za/rpp.do?PayUReference=${paymentData?.paymentReference}`;
    window.open(url, "_blank", "noopener,noreferrer");
    setStep("verify");
  };

  const handleVerify = async () => {
    setSubmitting(true);
    try {
      await verifyRentPayment({
        merchantReference: paymentData?.paymentReference,
        gatewayReference: gatewayRef.trim(),
      });
      setStep("confirmed");
      toast.success("Payment verified and confirmed!");
      if (onSuccess) {
        setTimeout(() => {
          handleClose();
          onSuccess();
        }, 1500);
      }
    } catch (err) {
      toast.error(err.message || "Payment verification failed. Please ensure the transaction is completed on PayU.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!invoice) {
    return (
      <ModalShell
        modalRef={modalRef}
        icon="ki-wallet"
        title="Rent Payment Settlement"
        onClose={handleClose}
        width={640}
      >
        <div className="d6m-empty">No invoice selected for payment.</div>
      </ModalShell>
    );
  }

  const invTotal = invoice.invoiceTotalAmount || invoice.amountDue || invoice.totalAmount || invoice.rentAmount || invoice.amount || 0;
  const serviceChargeComp = Array.isArray(invoice.components)
    ? invoice.components.find(c => (c.type || "").toLowerCase().includes("service") || (c.code || "").toLowerCase().includes("service"))?.amount
    : null;
  const serviceCharge = serviceChargeComp ?? (invoice.serviceCharge || 0);
  const rentBase = invoice.rentAmount || invoice.amount || (invTotal - serviceCharge);
  const txnCharge = paymentData?.transactionCharge ?? 0;
  const grandTotal = paymentData?.paymentTotalAmount ?? (Number(invTotal) + Number(txnCharge));

  return (
    <ModalShell
      modalRef={modalRef}
      icon="ki-wallet"
      title="Rent Payment Settlement"
      onClose={handleClose}
      closeDisabled={submitting}
      width={640}
      subheader={
        <div className="px-6 py-2 bg-light border-bottom border-gray-100 d-flex align-items-center justify-content-between">
          <span className="fs-8 text-gray-600">
            {invoice.buildingName || "Assigned Property"} • {invoice.period || "Current Term"}
          </span>
          <StatusChip color="#0EA84A">PayU Gateway</StatusChip>
        </div>
      }
      footer={
        step === "confirmed" ? (
          <button type="button" className="btn btn-success d6-mbtn" onClick={handleClose}>
            Done
          </button>
        ) : startError ? (
          <button type="button" className="btn btn-light d6-mbtn" onClick={handleClose}>
            Close
          </button>
        ) : !loading ? (
          <div className="d-flex align-items-center justify-content-between w-100 flex-wrap gap-2">
            <button type="button" className="btn btn-light d6-mbtn" onClick={handleClose} disabled={submitting}>
              Cancel
            </button>
            <div className="d-flex align-items-center gap-2">
              <button
                type="button"
                className="btn btn-light-success d6-mbtn"
                onClick={handleOpenPayU}
              >
                <i className="ki-outline ki-exit-right-corner fs-5 me-2.5" />
                Proceed to PayU
              </button>
              <button
                type="button"
                className="btn btn-success d6-mbtn"
                onClick={handleVerify}
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Verifying…
                  </>
                ) : (
                  <>
                    <i className="ki-outline ki-shield-tick fs-5 me-2.5" />
                    Verify Payment
                  </>
                )}
              </button>
            </div>
          </div>
        ) : null
      }
    >
      {loading ? (
        <div className="d-flex flex-column align-items-center justify-content-center py-12">
          <span className="spinner-border spinner-border-lg text-success mb-3" />
          <span className="d6m-name fs-7">Preparing secure payment checkout…</span>
          <span className="d6m-muted fs-8 mt-1">Generating payment reference from gateway</span>
        </div>
      ) : startError ? (
        <div className="text-center py-8">
          <div className="symbol symbol-70px symbol-circle bg-light-danger text-danger d-inline-flex align-items-center justify-content-center mb-3">
            <i className="ki-outline ki-information-5 fs-2tx text-danger" />
          </div>
          <h4 className="d6-title fs-4 text-gray-900 mb-2">Payment could not be started</h4>
          <p className="text-gray-600 fs-7 mb-0">{startError}</p>
        </div>
      ) : step === "confirmed" ? (
        <div className="text-center py-8">
          <div className="symbol symbol-70px symbol-circle bg-light-success text-success d-inline-flex align-items-center justify-content-center mb-3">
            <i className="ki-outline ki-check-circle fs-2tx text-success" />
          </div>
          <h4 className="d6-title fs-4 text-gray-900 mb-2">Payment Confirmed!</h4>
          <p className="text-gray-600 fs-7 mb-4">
            Your payment of <strong>{fmtMoney(grandTotal)}</strong> has been verified and confirmed.
          </p>
          <span className="badge bg-light-success text-success fs-8 px-4 py-1.5 fw-bold rounded-pill">
            Transaction Settled
          </span>
        </div>
      ) : (
        <div className="d-flex flex-column gap-4">
          {/* Amount Breakdown Card */}
          <div className="d6m-invoice">
            <div className="d-flex justify-content-between align-items-start mb-2">
              <div>
                <div className="d6m-muted fs-8">Total Payable Amount</div>
                <div className="d6m-amount text-success">{fmtMoney(grandTotal)}</div>
              </div>
              <StatusChip color="#0EA84A">Verified Invoice</StatusChip>
            </div>

            <div className="d6m-lines">
              <div className="d6m-line">
                <span>Base Rent</span>
                <span className="fw-semibold text-gray-900">{fmtMoney(rentBase)}</span>
              </div>
              {serviceCharge > 0 && (
                <div className="d6m-line">
                  <span>Estate Service Charge</span>
                  <span className="fw-semibold text-gray-900">{fmtMoney(serviceCharge)}</span>
                </div>
              )}
              <div className="d6m-line">
                <span>Gateway &amp; Transaction Fee</span>
                <span className="fw-semibold text-gray-900">{fmtMoney(txnCharge)}</span>
              </div>
              <div className="d6m-line d6m-line-total">
                <span>Total Amount Due</span>
                <span className="text-success">{fmtMoney(grandTotal)}</span>
              </div>
            </div>
          </div>

          {/* Payment Reference Box */}
          <div className="d6-card p-3.5 bg-light border border-gray-200">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <span className="d6m-muted fs-9 text-uppercase tracking-wider fw-bold">Merchant Reference</span>
              <button
                type="button"
                className="btn btn-link btn-color-success p-0 fs-8 fw-semibold text-decoration-none d-flex align-items-center gap-1"
                onClick={() => handleCopy(paymentData?.paymentReference)}
              >
                <i className={`ki-outline ${copied ? "ki-check" : "ki-copy"} fs-6`} />
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <div className="font-monospace text-gray-900 fw-bold fs-7">
              {paymentData?.paymentReference || "—"}
            </div>
          </div>

          {/* Instruction Note */}
          <div className="d6-card p-3.5 border border-primary border-dashed bg-light-primary bg-opacity-20 d-flex gap-3 align-items-start">
            <i className="ki-outline ki-information-5 fs-3 text-primary mt-0.5" />
            <div className="fs-8 text-gray-700">
              <strong>Settlement Steps:</strong><br />
              1. Click <strong>Proceed to PayU</strong> to complete payment via card, USSD or bank transfer.<br />
              2. Return here and click <strong>Verify Payment</strong> to instantly settle your invoice.
            </div>
          </div>

          {/* Gateway Reference Input */}
          <Field label="Gateway Reference (Optional PayU Reference)" hint="Enter if you received an external transaction reference from PayU.">
            <input
              type="text"
              className="d6m-input"
              placeholder="e.g. PAY_REF_FROM_PAYU"
              value={gatewayRef}
              onChange={e => setGatewayRef(e.target.value)}
            />
          </Field>
        </div>
      )}
    </ModalShell>
  );
}

/**
 * Foundation Support Donation Modal
 */
function FoundationSupportModal({ modalRef, onClose }) {
  const { hide } = useBootstrapModal(modalRef);
  const tenantId = getTenantId();
  const [amount, setAmount] = useState("5000");
  const [custom, setCustom] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [started, setStarted] = useState(null);
  const [gatewayRef, setGatewayRef] = useState("");

  const handleClose = () => {
    hide();
    setCustom("");
    setAmount("5000");
    setStarted(null);
    setGatewayRef("");
    onClose?.();
  };

  const finalAmount = custom ? Number(custom) : Number(amount);

  const handleDonate = async () => {
    if (!finalAmount || finalAmount <= 0) {
      toast.error("Please enter a valid donation amount.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await startFoundationSupportPayment({ amount: finalAmount, tenantId });
      if (res.checkoutUrl) {
        window.open(res.checkoutUrl, "_blank", "noopener,noreferrer");
      }
      setStarted(res);
    } catch (err) {
      toast.error(err.message || "Failed to start donation.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyDonation = async () => {
    setSubmitting(true);
    try {
      await verifyFoundationSupportPayment({
        merchantReference: started?.paymentReference,
        gatewayReference: gatewayRef.trim(),
      });
      toast.success("Thank you! Your donation has been confirmed.");
      handleClose();
    } catch (err) {
      toast.error(err.message || "Donation verification failed. Please ensure the transaction is completed on PayU.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell
      modalRef={modalRef}
      icon="ki-heart"
      title="Foundation Support & Donation"
      onClose={handleClose}
      closeDisabled={submitting}
      width={520}
      footer={
        <>
          <button type="button" className="btn btn-light d6-mbtn" onClick={handleClose} disabled={submitting}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-success d6-mbtn px-5"
            onClick={started ? handleVerifyDonation : handleDonate}
            disabled={submitting || finalAmount <= 0}
          >
            {submitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Processing…
              </>
            ) : started ? (
              <>
                <i className="ki-outline ki-shield-tick fs-5 me-2.5 text-white" />
                Verify Donation
              </>
            ) : (
              <>
                <i className="ki-outline ki-exit-right-corner fs-5 me-1.5 text-white" />
                Proceed to Checkout
              </>
            )}
          </button>
        </>
      }
    >
      {started ? (
        <div className="d-flex flex-column gap-4">
          <div className="d6-card p-3.5 bg-light border border-gray-200">
            <span className="d6m-muted fs-9 text-uppercase tracking-wider fw-bold">Merchant Reference</span>
            <div className="font-monospace text-gray-900 fw-bold fs-7">{started.paymentReference || "—"}</div>
          </div>
          <div className="fs-8 text-gray-700">
            Complete your donation on PayU, then return here and click <strong>Verify Donation</strong>.
          </div>
          <Field label="Gateway Reference (Optional PayU Reference)" hint="Enter if you received an external transaction reference from PayU.">
            <input
              type="text"
              className="d6m-input"
              placeholder="e.g. PAY_REF_FROM_PAYU"
              value={gatewayRef}
              onChange={e => setGatewayRef(e.target.value)}
            />
          </Field>
        </div>
      ) : (
      <div className="d-flex flex-column gap-4">
        <div className="d6m-muted">
          Support estate community outreach, environmental beautification, and welfare initiatives.
        </div>

        <div>
          <label className="d-block fs-8 fw-semibold text-gray-700 mb-2">Select Contribution Amount</label>
          <div className="d-flex gap-1.5 flex-wrap">
            {["2000", "5000", "10000", "25000", "50000"].map(v => (
              <button
                key={v}
                type="button"
                className={`btn btn-sm py-1.5 px-3 fs-8 rounded-pill transition-all ${
                  !custom && amount === v ? "btn-success shadow-xs" : "btn-light text-gray-700 border border-gray-200"
                }`}
                onClick={() => { setAmount(v); setCustom(""); }}
              >
                ₦{Number(v).toLocaleString()}
              </button>
            ))}
          </div>
        </div>

        <Field label="Or enter custom amount (NGN)">
          <div className="d6m-input-group">
            <span>₦</span>
            <input
              type="number"
              placeholder="e.g. 15000"
              value={custom}
              onChange={e => setCustom(e.target.value)}
              min="100"
            />
          </div>
        </Field>

        <div className="d6-card p-4 border border-success border-opacity-30 bg-light-success bg-opacity-20 d-flex justify-content-between align-items-center">
          <span className="text-gray-700 fs-8 fw-semibold">Total Contribution</span>
          <span className="text-success fs-4 fw-bolder">₦{finalAmount ? finalAmount.toLocaleString() : "0"}</span>
        </div>
      </div>
      )}
    </ModalShell>
  );
}

export default function TenantPayments() {
  const dashboardRef = useRef(null);
  const tenantId  = getTenantId();
  const isOccupant = isPrimaryOccupant();

  const [loading,   setLoading]    = useState(true);
  const [tab,       setTab]        = useState("invoices"); // "invoices" | "payments"
  const [invoices,  setInvoices]   = useState([]);
  const [payments,  setPayments]   = useState([]);
  const [invPage,   setInvPage]    = useState(0);
  const [payPage,   setPayPage]    = useState(0);
  const [invTotal,  setInvTotal]   = useState(0);
  const [payTotal,  setPayTotal]   = useState(0);
  const [invFilter, setInvFilter]  = useState("all");

  // Modal Refs
  const paymentModalRef = useRef(null);
  const donationModalRef = useRef(null);
  const paymentModal = useBootstrapModal(paymentModalRef);
  const donationModal = useBootstrapModal(donationModalRef);

  const [selectedInvoiceForPayment, setSelectedInvoiceForPayment] = useState(null);

  const fetchInvoices = useCallback(async (pg = 0) => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const data = await getRentInvoices({
        pageIndex: pg + 1,
        pageSize: PAGE_SIZE,
        status: invFilter !== "all" ? invFilter : "",
      });
      const list = parseArrayData(data);
      setInvoices(list);
      setInvTotal(data?.totalCount || data?.data?.totalCount || list.length);
      setInvPage(pg);
    } catch {
      toast.error("Failed to load invoices.");
    } finally {
      setLoading(false);
    }
  }, [tenantId, invFilter]);

  const fetchPayments = useCallback(async (pg = 0) => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const data = await getPaymentHistory({
        pageIndex: pg,
        pageSize: PAGE_SIZE,
      });
      const list = parseArrayData(data);
      setPayments(list);
      setPayTotal(data?.totalCount || data?.data?.totalCount || list.length);
      setPayPage(pg);
    } catch {
      toast.error("Failed to load payment history.");
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    if (tab === "invoices") fetchInvoices(0);
    else fetchPayments(0);
  }, [tab, fetchInvoices, fetchPayments]);

  useEffect(() => {
    if (loading) return undefined;
    const context = gsap.context(() => {
      gsap.fromTo(".gsap-hero", { opacity: 0, y: 18 }, { opacity: 1, y: 0, stagger: 0.06, duration: 0.55, ease: "power3.out", delay: 0.05, clearProps: "all" });
      gsap.fromTo(".gsap-card", { opacity: 0, y: 18 }, { opacity: 1, y: 0, stagger: 0.08, duration: 0.55, ease: "power2.out", delay: 0.2, clearProps: "all" });
    }, dashboardRef);
    return () => context.revert();
  }, [loading]);

  // Summary stats from invoices
  const totalOwed = invoices.filter(i => ["overdue", "pending", "created", "partial"].includes((i.status || "").toLowerCase()))
    .reduce((s, i) => s + (i.invoiceTotalAmount || i.amountDue || i.totalAmount || i.amount || 0), 0);
  const totalPaid = payments.reduce((s, p) => s + (p.amountPaid ?? p.amount ?? 0), 0);
  const overdueCount = invoices.filter(i => (i.status || "").toLowerCase() === "overdue").length;
  const firstUnpaidInvoice = invoices.find(i => (i.status || "").toLowerCase() !== "paid");

  const handleStartPayment = useCallback((inv) => {
    setSelectedInvoiceForPayment(inv);
    paymentModal.show();
  }, [paymentModal]);

  const { setHeaderConfig } = useTenantHeader();

  useEffect(() => {
    setHeaderConfig({
      title: "Payment Management",
      breadcrumb: "Payments",
      actions: null,
    });
    return () => setHeaderConfig({ title: "", breadcrumb: "", actions: null });
  }, [setHeaderConfig]);

  const invoiceStatuses = ["all", "Paid", "Pending", "Overdue", "Partial", "Cancelled"];

  return (
    <>
      <PagePreloader visible={loading} />

      <div ref={dashboardRef} className="app-container container-fluid py-6 px-6 px-lg-10">

        {/* Primary Occupant Info Alert */}
        {isOccupant && (
          <div className="d6-card p-4 mb-5 border border-primary border-dashed bg-light-primary bg-opacity-20 d-flex gap-3 align-items-center">
            <i className="ki-outline ki-information-5 fs-2 text-primary" />
            <div className="fs-8 text-gray-700">
              <strong>Primary Occupant Notice:</strong> Official property rent statements and rent settlement are registered to the leaseholder (Tenant).
            </div>
          </div>
        )}

        {/* Green Top Stats Section */}
        <div className="d6-kpi-brand p-6 mb-5">
          <div className="row g-4 g-xl-0 align-items-center">
            <div className="col-12 col-sm-6 col-xl-3 gsap-hero">
              <div className="px-4 py-2 border-end-xl border-white border-opacity-20 h-100">
                <div className="d-flex align-items-center">
                  <div className="symbol symbol-45px me-4 flex-shrink-0">
                    <span className="symbol-label bg-white bg-opacity-15 rounded-3">
                      <i className="ki-outline ki-wallet fs-2 text-white"></i>
                    </span>
                  </div>
                  <div className="flex-grow-1 min-w-0">
                    <div className="text-white text-opacity-80 fs-8 fw-semibold">Total Paid</div>
                    <div className="d6-num text-white my-0.5">{fmtMoney(totalPaid)}</div>
                    <div className="text-white text-opacity-70 fs-9">Lifetime payments</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-sm-6 col-xl-3 gsap-hero">
              <div className="px-4 py-2 border-end-xl border-white border-opacity-20 h-100">
                <div className="d-flex align-items-center">
                  <div className="symbol symbol-45px me-4 flex-shrink-0">
                    <span className="symbol-label bg-white bg-opacity-15 rounded-3">
                      <i className="ki-outline ki-bill fs-2 text-white"></i>
                    </span>
                  </div>
                  <div className="flex-grow-1 min-w-0">
                    <div className="text-white text-opacity-80 fs-8 fw-semibold">Outstanding Balance</div>
                    <div className="d6-num text-white my-0.5">{fmtMoney(totalOwed)}</div>
                    <div className="text-white text-opacity-70 fs-9">Pending settlement</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-sm-6 col-xl-3 gsap-hero">
              <div className="px-4 py-2 border-end-xl border-white border-opacity-20 h-100">
                <div className="d-flex align-items-center">
                  <div className="symbol symbol-45px me-4 flex-shrink-0">
                    <span className="symbol-label bg-white bg-opacity-15 rounded-3">
                      <i className="ki-outline ki-warning-2 fs-2 text-white"></i>
                    </span>
                  </div>
                  <div className="flex-grow-1 min-w-0">
                    <div className="text-white text-opacity-80 fs-8 fw-semibold">Overdue Invoices</div>
                    <div className="d6-num text-white my-0.5">{overdueCount}</div>
                    <div className="text-white text-opacity-70 fs-9">Requires immediate action</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-sm-6 col-xl-3 gsap-hero">
              <div className="px-4 py-2 h-100">
                <div className="d-flex align-items-center">
                  <div className="symbol symbol-45px me-4 flex-shrink-0">
                    <span className="symbol-label bg-white bg-opacity-15 rounded-3">
                      <i className="ki-outline ki-document fs-2 text-white"></i>
                    </span>
                  </div>
                  <div className="flex-grow-1 min-w-0">
                    <div className="text-white text-opacity-80 fs-8 fw-semibold">Total Invoices</div>
                    <div className="d6-num text-white my-0.5">{invTotal}</div>
                    <div className="text-white text-opacity-70 fs-9">Issued billing records</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Card */}
        <div className="d6-card p-6 mb-5 gsap-card">
          <CardHead
            title={tab === "invoices" ? "Invoices & Billing" : "Payment History"}
            subtitle={tab === "invoices" ? "Track and settle rent and estate service charge statements" : "Review past transactions and verified payments"}
            className="mb-5"
          >
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <div className="d6-seg">
                <button
                  type="button"
                  className={`fs-8 fw-bold ${tab === "invoices" ? "active" : ""}`}
                  onClick={() => setTab("invoices")}
                >
                  <i className="ki-outline ki-bill fs-5 me-1.5" />
                  Invoices
                </button>
                <button
                  type="button"
                  className={`fs-8 fw-bold ${tab === "payments" ? "active" : ""}`}
                  onClick={() => setTab("payments")}
                >
                  <i className="ki-outline ki-credit-cart fs-5 me-1.5" />
                  Payment History
                </button>
              </div>

              <button
                type="button"
                className="btn btn-light-success d6-mbtn"
                onClick={() => donationModal.show()}
              >
                <i className="ki-outline ki-heart fs-5 me-1 text-success" />
                <span>Foundation Support</span>
              </button>

              <button
                type="button"
                className="btn btn-success d6-mbtn"
                onClick={() => {
                  if (firstUnpaidInvoice) handleStartPayment(firstUnpaidInvoice);
                  else toast.success("All accounts in good standing. No outstanding payments.");
                }}
              >
                <i className="ki-outline ki-wallet fs-5 me-1 text-white" />
                <span>Make Payment</span>
              </button>

              {tab === "invoices" && (
                <select
                  className="d6m-input"
                  style={{ width: 140, minHeight: 40, height: 40, borderRadius: 999 }}
                  value={invFilter}
                  onChange={e => setInvFilter(e.target.value)}
                >
                  {invoiceStatuses.map(s => (
                    <option key={s} value={s}>{s === "all" ? "All Statuses" : s}</option>
                  ))}
                </select>
              )}
            </div>
          </CardHead>

          <div>
            {/* Invoices Tab */}
            {tab === "invoices" && (
              <div>
                {invoices.length === 0 && !loading && (
                  <div className="d6m-empty py-12">
                    <i className="ki-outline ki-bill fs-3x text-gray-300 d-block mb-3"></i>
                    <div className="d6-title text-gray-900 mb-1">No invoices found</div>
                    <div className="fs-8 text-gray-500">No rent or service charge invoices matching your criteria.</div>
                  </div>
                )}
                <div className="d-flex flex-column gap-3">
                  {invoices.map((inv, idx) => {
                    const isPaid = (inv.status || "").toLowerCase() === "paid";
                    const isOverdue = (inv.status || "").toLowerCase() === "overdue";
                    const total = inv.invoiceTotalAmount || inv.amountDue || inv.totalAmount || inv.amount;
                    const invKey = inv.rentInvoiceId || inv.id || idx;

                    return (
                      <div
                        key={invKey}
                        className="d6-card d6-list-card d-flex align-items-center mb-2 py-4 px-5 border-hover-success position-relative overflow-hidden cursor-pointer transition-all"
                        style={{ minHeight: 90 }}
                        onClick={() => !isPaid && handleStartPayment(inv)}
                      >
                        <div className={`position-absolute top-0 start-0 h-100 w-4px ${isPaid ? "bg-success" : isOverdue ? "bg-danger" : "bg-warning"}`}></div>
                        
                        <div className="d-flex align-items-center me-4" style={{ width: "380px", flexShrink: 0 }}>
                          <div className="symbol symbol-48px symbol-circle me-4 flex-shrink-0 d-flex align-items-center justify-content-center bg-light border border-gray-200" style={{ width: 48, height: 48 }}>
                            <i className="ki-outline ki-bill fs-2 text-success"></i>
                          </div>
                          <div className="d-flex flex-column overflow-hidden min-w-0">
                            <div className="d-flex align-items-center gap-2 mb-0.5 flex-wrap">
                              <span className="fs-6 fw-bold text-gray-900 text-truncate">{inv.buildingName || "Assigned Building"}</span>
                              <StatusChip color={INVOICE_DOT[(inv.status || "").toLowerCase()] || "#D97706"}>
                                {inv.status || "Pending"}
                              </StatusChip>
                              {inv.phaseName && (
                                <span className="badge bg-light text-gray-700 fs-9 fw-semibold rounded-pill px-2.5 py-0.5">
                                  {inv.phaseName}
                                </span>
                              )}
                            </div>
                            <div className="text-gray-500 fs-8 text-truncate">
                              Period: {inv.period || (inv.periodStart ? `${fmtDate(inv.periodStart)} — ${fmtDate(inv.periodEnd)}` : "Current Term")}
                            </div>
                          </div>
                        </div>

                        <div className="d-flex align-items-center gap-5 text-end ms-auto flex-shrink-0 flex-wrap">
                          <div className="d-none d-sm-block text-center">
                            <div className="text-gray-400 fs-9 mb-0.5">Base Rent</div>
                            <div className="fw-semibold text-gray-800 fs-8">{fmtMoney(inv.amount || (total - (inv.serviceCharge || 0)))}</div>
                          </div>
                          {inv.serviceCharge != null && (
                            <div className="d-none d-md-block text-center">
                              <div className="text-gray-400 fs-9 mb-0.5">Service Charge</div>
                              <div className="fw-semibold text-gray-700 fs-8">{fmtMoney(inv.serviceCharge)}</div>
                            </div>
                          )}
                          <div className="d-none d-sm-block text-center">
                            <div className="text-gray-400 fs-9 mb-0.5">Total Due</div>
                            <div className="fw-bold text-gray-900 fs-8">{fmtMoney(total)}</div>
                          </div>
                          <div className="d-none d-sm-block text-center">
                            <div className="text-gray-400 fs-9 mb-0.5">Due Date</div>
                            <div className={`fw-semibold fs-8 ${isOverdue ? "text-danger" : "text-gray-900"}`}>
                              {fmtDate(inv.dueDate)}
                            </div>
                          </div>
                          
                          {/* Actions */}
                          <div className="text-center ms-2">
                            {!isPaid ? (
                              <button
                                type="button"
                                className="btn btn-sm btn-success d6-mbtn py-1 px-3 fs-9"
                                onClick={(e) => { e.stopPropagation(); handleStartPayment(inv); }}
                              >
                                <i className="ki-outline ki-wallet fs-6 me-2 text-white" />
                                Pay Now
                              </button>
                            ) : inv.receiptUrl ? (
                              <a 
                                href={inv.receiptUrl} 
                                target="_blank" 
                                rel="noreferrer" 
                                className="d6-icon-btn" 
                                title="Download Receipt"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <i className="ki-outline ki-file-down fs-5 text-success" />
                              </a>
                            ) : (
                              <span className="badge bg-light-success text-success fw-bold fs-9 px-3 py-1.5 rounded-pill d-inline-flex align-items-center">
                                <i className="ki-outline ki-check-circle fs-6 me-1 text-success" /> Settled
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {invTotal > PAGE_SIZE && (
                  <div className="d-flex align-items-center justify-content-between mt-6 pt-4 border-top border-gray-100 flex-wrap gap-3">
                    <div className="text-gray-500 fs-8">
                      Showing {invTotal > 0 ? invPage * PAGE_SIZE + 1 : 0}–{Math.min((invPage + 1) * PAGE_SIZE, invTotal)} of {invTotal} invoices
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <button
                        type="button"
                        className="btn btn-sm btn-light d6-mbtn px-3"
                        style={{ height: 32 }}
                        disabled={invPage === 0}
                        onClick={() => fetchInvoices(invPage - 1)}
                      >
                        <i className="ki-outline ki-left fs-6 m-0"></i>
                      </button>
                      <span className="btn btn-sm btn-success d6-mbtn px-3 py-0" style={{ height: 32 }}>
                        {invPage + 1}
                      </span>
                      <button
                        type="button"
                        className="btn btn-sm btn-light d6-mbtn px-3"
                        style={{ height: 32 }}
                        disabled={(invPage + 1) * PAGE_SIZE >= invTotal}
                        onClick={() => fetchInvoices(invPage + 1)}
                      >
                        <i className="ki-outline ki-right fs-6 m-0"></i>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Payments Tab */}
            {tab === "payments" && (
              <div>
                {payments.length === 0 && !loading && (
                  <div className="d6m-empty py-12">
                    <i className="ki-outline ki-wallet fs-3x text-gray-300 d-block mb-3"></i>
                    <div className="d6-title text-gray-900 mb-1">No payment history found</div>
                    <div className="fs-8 text-gray-500">Your settled transactions will appear here.</div>
                  </div>
                )}
                <div className="d-flex flex-column gap-3">
                  {payments.map((p, i) => {
                    const title = p.buildingNumber ? `Building ${p.buildingNumber}` : (p.buildingName || "Estate Payment");
                    const paidVal = p.amountPaid ?? p.amount;
                    const statusStr = p.status || "Successful";
                    const isOk = statusStr.toLowerCase() === "successful";

                    return (
                      <div
                        key={p.id || i}
                        className="d6-card d6-list-card d-flex align-items-center mb-2 py-4 px-5 border-hover-success position-relative overflow-hidden cursor-pointer transition-all"
                        style={{ minHeight: 90 }}
                      >
                        <div className={`position-absolute top-0 start-0 h-100 w-4px ${isOk ? "bg-success" : "bg-warning"}`}></div>
                        
                        <div className="d-flex align-items-center me-4" style={{ width: "380px", flexShrink: 0 }}>
                          <div className="symbol symbol-48px symbol-circle me-4 flex-shrink-0 d-flex align-items-center justify-content-center bg-light-success text-success border border-success border-dashed" style={{ width: 48, height: 48 }}>
                            <i className="ki-outline ki-wallet fs-2 text-success"></i>
                          </div>
                          <div className="d-flex flex-column overflow-hidden min-w-0">
                            <div className="d-flex align-items-center gap-2 mb-0.5 flex-wrap">
                              <span className="fs-6 fw-bold text-gray-900 text-truncate">{title}</span>
                              <StatusChip color={isOk ? "#0EA84A" : "#D97706"}>
                                {statusStr}
                              </StatusChip>
                              {p.paymentMethod && (
                                <span className="badge bg-light text-gray-700 fs-9 fw-semibold rounded-pill px-2.5 py-0.5">
                                  {p.paymentMethod}
                                </span>
                              )}
                            </div>
                            <div className="text-gray-500 fs-8 text-truncate">
                              Ref: {p.gatewayReference || p.merchantReference || p.transactionRef || "—"} {p.dueDate ? `• Due: ${fmtDate(p.dueDate)}` : ""}
                            </div>
                          </div>
                        </div>

                        <div className="d-flex align-items-center gap-5 text-end ms-auto flex-shrink-0 flex-wrap">
                          <div className="d-none d-sm-block text-center">
                            <div className="text-gray-400 fs-9 mb-0.5">Date Settled</div>
                            <div className="fw-semibold text-gray-900 fs-8">{fmtDate(p.paidAt || p.paymentDate)}</div>
                          </div>
                          <div className="d-none d-sm-block text-center">
                            <div className="text-gray-400 fs-9 mb-0.5">Amount Paid</div>
                            <div className="fw-bold text-success fs-8">{fmtMoney(paidVal)}</div>
                          </div>
                          <div className="text-center ms-2">
                            {p.receiptUrl ? (
                              <a 
                                href={p.receiptUrl} 
                                target="_blank" 
                                rel="noreferrer" 
                                className="d6-icon-btn" 
                                title="Download Receipt"
                              >
                                <i className="ki-outline ki-file-down fs-5 text-success" />
                              </a>
                            ) : (
                              <span className="badge bg-light-success text-success fs-9 fw-semibold px-3 py-1.5 rounded-pill d-inline-flex align-items-center">
                                <i className="ki-outline ki-check-circle fs-6 me-1 text-success" /> Settled
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {payTotal > PAGE_SIZE && (
                  <div className="d-flex align-items-center justify-content-between mt-6 pt-4 border-top border-gray-100 flex-wrap gap-3">
                    <div className="text-gray-500 fs-8">
                      Showing {payTotal > 0 ? payPage * PAGE_SIZE + 1 : 0}–{Math.min((payPage + 1) * PAGE_SIZE, payTotal)} of {payTotal} transactions
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <button
                        type="button"
                        className="btn btn-sm btn-light d6-mbtn px-3"
                        style={{ height: 32 }}
                        disabled={payPage === 0}
                        onClick={() => fetchPayments(payPage - 1)}
                      >
                        <i className="ki-outline ki-left fs-6 m-0"></i>
                      </button>
                      <span className="btn btn-sm btn-success d6-mbtn px-3 py-0" style={{ height: 32 }}>
                        {payPage + 1}
                      </span>
                      <button
                        type="button"
                        className="btn btn-sm btn-light d6-mbtn px-3"
                        style={{ height: 32 }}
                        disabled={(payPage + 1) * PAGE_SIZE >= payTotal}
                        onClick={() => fetchPayments(payPage + 1)}
                      >
                        <i className="ki-outline ki-right fs-6 m-0"></i>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

      </div>

      {/* Payment Checkout Modal */}
      <PaymentModal
        modalRef={paymentModalRef}
        invoice={selectedInvoiceForPayment}
        onClose={() => setSelectedInvoiceForPayment(null)}
        onSuccess={() => {
          fetchInvoices(invPage);
          fetchPayments(0);
        }}
      />

      {/* Foundation Support Donation Modal */}
      <FoundationSupportModal 
        modalRef={donationModalRef}
        onClose={() => {}} 
      />
    </>
  );
}
